"use client";

import { useState, useCallback, useMemo, useRef, useEffect } from "react";
import {
  AUTOMATIC_STREAK_STATUS,
  AUTOMATIC_STREAK_QUEUE_LEAVE_REASON,
  type AutomaticStreakQueueLeaveReason,
  ConnectionStatus,
  Decimal,
  type StreakResponseV2,
  type StreakStatusResponse,
  type StreakSummaryResponse,
  type StreakSummaryResponseV2,
} from "@cede/types";
import { AutomaticStreakCard } from "./AutomaticStreakCard";
import {
  AutomaticStreakConfigForm,
  type AutomaticStreakConfigFormData,
  AUTOMATIC_STREAK_CONFIG_FORM_ID,
} from "./AutomaticStreakConfigForm";
import { AutomaticStreakProgress } from "./AutomaticStreakProgress";
import {
  AutomaticStreakCardV2,
  AutomaticStreakProgressV2,
  AutomaticStreakConfigFormV2,
  type AutomaticStreakConfigFormV2Data,
  AUTOMATIC_STREAK_CONFIG_FORM_V2_ID,
} from "./v2";
import { BlockedAccessMessage } from "@/components/features/trade/InvalidAccess/BlockedAccessMessage";
import { AnimatedCedeLogo } from "@/components/icons";
import { MainFooter } from "@/components/layout/MainFooter/MainFooter";
import { Page } from "@/components/layout/Page/Page";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { ROUTES } from "@/constants/routes";
import { useCampaign, useTradeCampaignAccess, useDevice } from "@/hooks";
import { useAccounts } from "@/hooks/accounts";
import {
  useAutomaticStreakConfig,
  useCreateStreak,
  useJoinStreakQueue,
  useLeaveStreakQueue,
  useUserStreakQueueItem,
  usePriorityTickets,
  useIncreaseQueuePriority,
  useUserStreaks,
  useCancelStreak,
  useStreakStatus,
  useAutomaticStreakCgu,
  invalidateEpochLimitQuery,
} from "@/hooks/automatic-streak";
import { useSocialAccountRequirement } from "@/hooks/socials/useSocialAccountRequirement";
import { useActiveTradingPair } from "@/hooks/trading";
import { cn } from "@/lib/utils";
import { useConnectStore, STEPS, FLOW_TYPES } from "@/store/connectStore";
import { useTradingViewStore } from "@/store/useTradingViewStore";
import { isManualBlockerAllowingAutopilot } from "@/utils/automatic-streak";
import { useQueryClient } from "@tanstack/react-query";
import {
  Bot,
  AlertTriangle,
  Loader2,
  AlertCircle,
  FileText,
  Clock,
  ChevronLeft,
  Link as LinkIcon,
  Zap,
} from "lucide-react";
import Link from "next/link";
import { useParams } from "next/navigation";

/**
 * Maps a backend autopilot-queue leave reason to a short user-facing label.
 * `USER_REQUESTED` is filtered out server-side (voluntary leaves never surface
 * as kicks), so we only need to describe the system-eviction subset here.
 * Returns the raw reason for forward compatibility if the backend ever adds a
 * new value the UI doesn't know about yet.
 */
function describeQueueKickReason(reason: AutomaticStreakQueueLeaveReason): string {
  switch (reason) {
    case AUTOMATIC_STREAK_QUEUE_LEAVE_REASON.INSUFFICIENT_BALANCE:
      return "your balance was too low when a slot opened up";
    case AUTOMATIC_STREAK_QUEUE_LEAVE_REASON.EXCHANGE_ACCOUNT_DISCONNECTED:
      return "your exchange account was disconnected when a slot opened up";
    case AUTOMATIC_STREAK_QUEUE_LEAVE_REASON.MISSING_SOCIAL_ACCOUNTS:
      return "a required social account was no longer linked when a slot opened up";
    case AUTOMATIC_STREAK_QUEUE_LEAVE_REASON.INVALID_CONFIGURATION:
      return "your saved autopilot config no longer matches this campaign — please re-join with valid parameters";
    case AUTOMATIC_STREAK_QUEUE_LEAVE_REASON.USER_REQUESTED:
      return reason;
  }
}

/**
 * Resolves the label of the submit button.
 *
 * Two flows feed this button:
 * - **Queue join** (`queueSystemEnabled === true`): `useJoinStreakQueue` is
 *   active, so `isJoining` drives the spinner copy.
 * - **Direct create** (`queueSystemEnabled === false` — V1, or V2 with
 *   `disableQueueSystem`): `useCreateStreak` is active, so `isCreating`
 *   drives the spinner copy.
 *
 * `isSubmitDebounced` is shared and applies in both flows after submit.
 */
function getSubmitButtonLabel(params: {
  queueSystemEnabled: boolean;
  isCreating: boolean;
  isJoining: boolean;
  isSubmitDebounced: boolean;
}): string {
  const { queueSystemEnabled, isCreating, isJoining, isSubmitDebounced } = params;

  if (queueSystemEnabled) {
    if (isJoining) return "Joining queue...";
    if (isSubmitDebounced) return "Please wait...";
    return "Join Autopilot Queue";
  }

  if (isCreating) return "Creating...";
  if (isSubmitDebounced) return "Please wait...";
  return "Start Autopilot";
}

/**
 * Type guard to check if a streak is V2
 */
function isV2Streak(streak: StreakStatusResponse | StreakResponseV2): streak is StreakResponseV2 {
  return "version" in streak && streak.version === "V2";
}

/**
 * Type guard to check if a streak summary is V2
 */
function isV2StreakSummary(streak: StreakSummaryResponse | StreakSummaryResponseV2): streak is StreakSummaryResponseV2 {
  return "version" in streak && streak.version === "V2";
}

/**
 * Autopilot panels are prose-and-form, so they need real gutters. Set here
 * rather than on `Page`, whose tighter default is shared with the manual
 * spot/perps trade form and must stay as it is.
 */
const AUTOPILOT_PAGE_PADDING = "p-4";

const AUTOMATIC_STREAK_WIDGET_VIEW = {
  CGU: "cgu",
  CONFIG: "config",
  QUEUE_WAITING: "queue-waiting",
  PROGRESS: "progress",
} as const;

type AutomaticStreakWidgetView = (typeof AUTOMATIC_STREAK_WIDGET_VIEW)[keyof typeof AUTOMATIC_STREAK_WIDGET_VIEW];

interface CguCheckboxItemProps {
  id: string;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  icon: React.ReactNode;
  title: string;
  description: string;
}

const CguCheckboxItem = ({ id, checked, onCheckedChange, icon, title, description }: CguCheckboxItemProps) => {
  return (
    <label
      htmlFor={id}
      className={cn(
        "flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-all duration-200",
        "hover:border-cede-primary/30 hover:bg-cede-primary/5",
        checked ? "border-cede-primary bg-cede-primary/10" : "border-cede-border-container bg-cede-bg-component",
      )}
    >
      <Checkbox id={id} checked={checked} onCheckedChange={onCheckedChange} className="mt-0.5" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-0.5">
          {icon}
          <span className="font-medium text-sm text-cede-text-primary">{title}</span>
        </div>
        <p className="text-xs text-cede-text-secondary leading-relaxed">{description}</p>
      </div>
    </label>
  );
};

export const AutomaticStreakWidget = () => {
  const queryClient = useQueryClient();
  const { id: campaignName } = useParams<{ id: string }>();
  const { campaign, currentEpoch, nextEpoch } = useCampaign(campaignName);
  const { exchange, isWidgetInitialized } = useTradingViewStore();

  // The TradingView iframe is the only writer of the store's `pairSymbol` —
  // for coming-soon campaigns the iframe never fires `UPDATE_PAIR` (no chart
  // context), so reading the store directly would trip the "Please select a
  // trading pair first" gate even though the campaign already exposes a
  // default token. `useActiveTradingPair` returns the store value when it's
  // valid for the current campaign and otherwise derives a campaign default
  // (`${campaign.tokenSymbol}/<stable>`) from the exchange's markets — so it
  // is the single source of truth for the pair the widget should use.
  const { activePairSymbol: pairSymbol } = useActiveTradingPair({ campaignData: campaign });
  const { isMobile } = useDevice();
  const { setActiveStep, setOpenConnectModal, setExchangeIds, setFlowType } = useConnectStore();

  // Check if user has required social accounts
  const { shouldBlockTrading: isMissingSocials, openSocialLinkingModal } = useSocialAccountRequirement({
    requiredSocialAccounts: campaign?.requiredSocialAccounts,
  });

  // Get autopilot config (from campaign or global fallback)
  const {
    autopilotConfig,
    configVersion,
    isLoading: isConfigLoading,
    error: configError,
    isEpochLimitReached,
    isQueueDisabled,
  } = useAutomaticStreakConfig(campaignName);

  // Handler to open connect modal with campaign's exchanges
  const handleConnectExchange = useCallback(() => {
    setFlowType(FLOW_TYPES.READY_TO_USE);
    setActiveStep(STEPS.ChooseCEX);
    setExchangeIds(campaign?.exchangeIds || []);
    setOpenConnectModal(true);
  }, [campaign?.exchangeIds, setFlowType, setActiveStep, setExchangeIds, setOpenConnectModal]);

  // Connection status of the account autopilot would trade on. An account
  // flagged `reconnectionRequired` has API keys the backend can no longer use
  // (missing trade permission, IP-restricted, or revoked) — starting autopilot
  // on it would be stopped immediately server-side, so we surface a reconnect
  // prompt instead. Absence in the map (hubUser still resolving) safely reads
  // as "not flagged" — the same accounts are disabled in every account picker.
  const { accountsByIds } = useAccounts();
  const selectedAccount = exchange?.accountId ? accountsByIds[exchange.accountId] : undefined;
  const needsReconnection = selectedAccount?.status === ConnectionStatus.RECONNECTION_REQUIRED;

  // Relink the flagged account (same flow the invalid-permission snackbar uses).
  const handleReconnectAccount = useCallback(() => {
    if (!exchange?.exchangeId) return;
    setFlowType(FLOW_TYPES.RELINK);
    setExchangeIds([exchange.exchangeId]);
    setActiveStep(STEPS.ChooseCEX);
    setOpenConnectModal(true);
  }, [exchange?.exchangeId, setFlowType, setExchangeIds, setActiveStep, setOpenConnectModal]);

  // Use the same access check as manual trading - this returns RESTRICTED_PARTICIPANTS, CAMPAIGN_PAUSED, etc.
  const {
    access,
    reason,
    isLoading: isAccessLoading,
    defiAddresses,
    nextAccessDate,
  } = useTradeCampaignAccess({
    campaignName: campaignName ?? "",
  });

  // State for the flow
  const [selectedStreakId, setSelectedStreakId] = useState<string | null>(null);
  const [isFormValid, setIsFormValid] = useState(false);

  // 5-second debounce after submitting to prevent rapid re-submissions
  const SUBMIT_DEBOUNCE_MS = 5000;
  const [isSubmitDebounced, setIsSubmitDebounced] = useState(false);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Clean up debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  // CGU - check if user has already accepted (from backend)
  const {
    riskAcknowledged,
    automatedTradingAcknowledged,
    termsAndConditionsAccepted,
    canProceed: cguCanProceed,
    isSubmitting: isCguSubmitting,
    isAlreadyAcknowledged,
    setRiskAcknowledged,
    setAutomatedTradingAcknowledged,
    setTermsAndConditionsAccepted,
    handleConfirm: handleCguConfirm,
  } = useAutomaticStreakCgu();

  // Hooks for data and actions
  const { createStreak, isCreating } = useCreateStreak({
    version: configVersion,
    campaignName: campaignName as string,
    onSuccess: (streakId) => {
      setSelectedStreakId(streakId);
    },
  });
  const { joinStreakQueue, isJoining } = useJoinStreakQueue();
  const { leaveStreakQueue, isLeaving } = useLeaveStreakQueue();
  const {
    isInQueue: isInStreakQueue,
    lastKickReason,
    priorityCounter,
  } = useUserStreakQueueItem({
    campaignName: campaignName ?? "",
    enabled: configVersion === 2 && !!campaignName && !isQueueDisabled,
  });
  const { totalTickets: priorityTicketBalance } = usePriorityTickets({
    enabled: configVersion === 2 && !isQueueDisabled,
  });
  const { increaseQueuePriority, isIncreasing } = useIncreaseQueuePriority();
  const {
    activeStreaks,
    hasActiveStreak,
    isLoading: isStreaksLoading,
  } = useUserStreaks({
    campaignName: campaignName as string,
    version: configVersion,
  });
  const { cancelStreak, isCancelling } = useCancelStreak({
    version: configVersion,
    onSuccess: () => {
      if (campaignName) {
        invalidateEpochLimitQuery(queryClient, campaignName);
      }
    },
  });
  const { streak: selectedStreak } = useStreakStatus({
    streakId: selectedStreakId,
    enabled: !!selectedStreakId,
    version: configVersion,
  });

  // Epoch we pin the V2 config form to. Falls back to the next upcoming epoch
  // when no current one exists yet (campaign hasn't started) — the queue
  // picker will materialize the streak against whatever epoch is active when
  // the user is selected.
  const effectiveEpoch = currentEpoch ?? nextEpoch;

  // V1 must run for the active epoch (it creates a streak immediately); V2
  // with the queue can use an upcoming epoch since the picker materializes
  // later. When the queue is disabled, V2 mirrors V1: direct creation needs
  // an active epoch.
  const requiredEpochForVersion = configVersion === 2 && !isQueueDisabled ? effectiveEpoch : currentEpoch;
  const canStartStreak = exchange?.accountId && pairSymbol?.pairSymbol && requiredEpochForVersion;
  const missingDataMessage = !exchange?.accountId
    ? "Please connect an exchange account first"
    : !pairSymbol?.pairSymbol
      ? "Please select a trading pair first"
      : !requiredEpochForVersion
        ? configVersion === 2 && !isQueueDisabled
          ? "No upcoming epoch available for this campaign"
          : "No active epoch available"
        : null;

  // Separate streaks into current epoch and next epoch for proper ordering and grouping
  // Note: Use explicit null/undefined check because epoch ID can be 0 (which is falsy but valid)
  const { currentEpochStreaks, nextEpochStreaks } = useMemo(() => {
    if (currentEpoch?.id == null) return { currentEpochStreaks: [], nextEpochStreaks: [] };
    return {
      currentEpochStreaks: activeStreaks.filter((s) => s.epochId === currentEpoch.id),
      nextEpochStreaks: activeStreaks.filter((s) => s.epochId > currentEpoch.id),
    };
  }, [activeStreaks, currentEpoch?.id]);

  // Determine current view
  const currentView = useMemo((): AutomaticStreakWidgetView => {
    // If there's a selected streak, show progress (regardless of status)
    // User must click "Start New Autopilot" to go back to config
    if (selectedStreakId && selectedStreak) {
      return AUTOMATIC_STREAK_WIDGET_VIEW.PROGRESS;
    }

    // If user has an active streak, show progress for the first one
    if (hasActiveStreak && (currentEpochStreaks.length > 0 || nextEpochStreaks.length > 0)) {
      return AUTOMATIC_STREAK_WIDGET_VIEW.PROGRESS;
    }

    // If user already opted into the V2 queue, show the waiting UI instead of the form.
    // When the queue is disabled on the config, this branch is unreachable
    // (the queue hook is gated off and isInStreakQueue stays false).
    if (configVersion === 2 && !isQueueDisabled && isInStreakQueue) {
      return AUTOMATIC_STREAK_WIDGET_VIEW.QUEUE_WAITING;
    }

    // If CGU not acknowledged, show CGU
    if (!isAlreadyAcknowledged) {
      return AUTOMATIC_STREAK_WIDGET_VIEW.CGU;
    }

    // Otherwise show config
    return AUTOMATIC_STREAK_WIDGET_VIEW.CONFIG;
  }, [
    selectedStreakId,
    selectedStreak,
    hasActiveStreak,
    currentEpochStreaks,
    nextEpochStreaks,
    isInStreakQueue,
    configVersion,
    isQueueDisabled,
    isAlreadyAcknowledged,
  ]);

  // Handle CGU confirmation
  const handleCguSubmit = useCallback(async () => {
    await handleCguConfirm();
  }, [handleCguConfirm]);

  const startSubmitDebounce = useCallback(() => {
    setIsSubmitDebounced(true);
    debounceTimerRef.current = setTimeout(() => {
      setIsSubmitDebounced(false);
    }, SUBMIT_DEBOUNCE_MS);
  }, []);

  // Handle config form submission (V1)
  const handleConfigSubmitV1 = useCallback(
    async (formData: AutomaticStreakConfigFormData) => {
      if (!campaignName || !currentEpoch || !exchange?.accountId || !pairSymbol?.pairSymbol) {
        return;
      }

      startSubmitDebounce();

      try {
        await createStreak({
          campaignName: campaignName as string,
          epochId: currentEpoch.id,
          exchangeAccountId: parseInt(exchange.accountId, 10),
          pairSymbol: pairSymbol.pairSymbol,
          totalTrades: formData.totalTrades,
          volumePerCycle: formData.volumePerCycle,
          intervalSeconds: formData.intervalSeconds,
          scheduledStartTime: formData.scheduledStartTime.toISOString(),
          alsoRunForNextEpoch: formData.alsoRunForNextEpoch,
        });
      } catch {
        // Error is already handled by useCreateStreak's onError callback (snackbar + cache invalidation)
      }
    },
    [campaignName, currentEpoch, exchange?.accountId, pairSymbol?.pairSymbol, createStreak, startSubmitDebounce],
  );

  // Handle config form submission (V2)
  // - Queue enabled (default): opt the user into the autopilot queue. The
  //   backend's queue tick materializes the streak once a slot opens up.
  // - Queue disabled (`disableQueueSystem: true`): create the streak
  //   immediately against the current epoch via the same V2 createStreak
  //   path the queue picker would have used.
  const handleConfigSubmitV2 = useCallback(
    async (formData: AutomaticStreakConfigFormV2Data) => {
      if (!campaignName || !exchange?.accountId || !pairSymbol?.pairSymbol) {
        return;
      }

      startSubmitDebounce();

      try {
        if (isQueueDisabled) {
          if (currentEpoch?.id == null) return;
          await createStreak({
            campaignName: campaignName as string,
            epochId: currentEpoch.id,
            exchangeAccountId: parseInt(exchange.accountId, 10),
            pairSymbol: pairSymbol.pairSymbol,
            totalTargetVolume: new Decimal(formData.totalTargetVolume),
          });
          return;
        }

        await joinStreakQueue({
          campaignName: campaignName as string,
          exchangeAccountId: parseInt(exchange.accountId, 10),
          pairSymbol: pairSymbol.pairSymbol,
          totalTargetVolume: new Decimal(formData.totalTargetVolume),
          priorityTicketsToSpend: formData.priorityTicketsToSpend,
        });
      } catch {
        // Error is already handled by the underlying hook's onError callback (snackbar)
      }
    },
    [
      campaignName,
      exchange?.accountId,
      pairSymbol?.pairSymbol,
      isQueueDisabled,
      currentEpoch?.id,
      createStreak,
      joinStreakQueue,
      startSubmitDebounce,
    ],
  );

  // Handle cancellation
  const handleCancelStreak = useCallback(
    async (streakId: string) => {
      await cancelStreak(streakId);
      setSelectedStreakId(null);
    },
    [cancelStreak],
  );

  // Handle clicking on an active streak card
  const handleStreakCardClick = useCallback((streakId: string) => {
    setSelectedStreakId(streakId);
  }, []);

  // Get the active streak to show
  const activeStreakToShow = selectedStreak ?? null;

  // Check if there's a pending streak for a future epoch (used to hide "Start New Autopilot")
  // Note: Use explicit null/undefined check because epoch ID can be 0 (which is falsy but valid)
  const pendingNextEpochStreak = useMemo(() => {
    if (currentEpoch?.id == null) return null;
    return (
      activeStreaks.find((s) => s.epochId > currentEpoch.id && s.status === AUTOMATIC_STREAK_STATUS.PENDING) ?? null
    );
  }, [activeStreaks, currentEpoch?.id]);

  // No exchange connected - show this FIRST, before loading state
  // This way users without an exchange don't have to wait for TradingView widget initialization
  if (!exchange?.accountId) {
    return (
      <Page displayFlex className={AUTOPILOT_PAGE_PADDING}>
        <div className="flex flex-col flex-1 items-center justify-center gap-4">
          <div className="p-3 rounded-full bg-cede-primary/20">
            <LinkIcon className="w-8 h-8 text-cede-primary" />
          </div>
          <div className="text-center">
            <h3 className="font-medium text-cede-text-primary mb-1">Connect an Exchange</h3>
            <p className="text-sm text-cede-text-secondary max-w-xs">
              To use the Autopilot feature, you need to connect an exchange account for this campaign first.
            </p>
          </div>
          <Button size={isMobile ? "large" : "medium"} onClick={handleConnectExchange}>
            Connect Exchange
          </Button>
        </div>
      </Page>
    );
  }

  // Loading state - show while config/streaks are loading OR while trading widget is initializing
  // Note: We no longer wait for pairSymbol - if it's missing, we show the UI with a warning message
  if (isConfigLoading || isStreaksLoading || isAccessLoading || !isWidgetInitialized) {
    return (
      <Page displayFlex className={AUTOPILOT_PAGE_PADDING}>
        <div className="flex flex-col flex-1 items-center justify-center gap-3 text-cede-text-primary">
          <Loader2 className="w-6 h-6 animate-spin text-cede-text-secondary" />
          <span className="text-sm text-cede-text-secondary">Loading autopilot data...</span>
        </div>
      </Page>
    );
  }

  // Blocked state — only render the access-denied screen for reasons that
  // genuinely block autopilot (banned, restricted, paused, ...). Volume
  // saturation still allows access: the user can queue autopilot for the
  // next epoch. FUTURE campaigns aren't classified as blockers at all
  // (handled upstream in `useTradeCampaignAccess`).
  if (access === false && reason && !isManualBlockerAllowingAutopilot(reason)) {
    return (
      <Page displayFlex className={AUTOPILOT_PAGE_PADDING}>
        <BlockedAccessMessage
          invalidAccessReason={reason}
          defiAddresses={defiAddresses ?? []}
          nextAccessDate={nextAccessDate}
          referralOnlyExchangeIds={campaign?.referralOnlyExchangeIds ?? undefined}
        />
      </Page>
    );
  }

  // Config error state - CMS unavailable
  if (configError || !autopilotConfig) {
    return (
      <Page displayFlex className={AUTOPILOT_PAGE_PADDING}>
        <div className="flex flex-col flex-1 items-center justify-center gap-3">
          <div className="p-3 rounded-full bg-cede-error/20">
            <AlertCircle className="w-6 h-6 text-cede-error" />
          </div>
          <div className="text-center">
            <h3 className="font-medium text-cede-text-primary mb-1">Configuration Unavailable</h3>
            <p className="text-sm text-cede-text-secondary">
              Unable to load autopilot configuration. Please try again later.
            </p>
          </div>
        </div>
      </Page>
    );
  }

  // CGU View - inline checkboxes
  if (currentView === AUTOMATIC_STREAK_WIDGET_VIEW.CGU) {
    return (
      <Page displayFlex className={AUTOPILOT_PAGE_PADDING}>
        <div className="flex flex-col flex-1">
          {/* Header */}
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 rounded-lg bg-cede-warning/20">
              <AlertTriangle className="w-5 h-5 text-cede-warning" />
            </div>
            <div>
              <h5 className="h5 text-white">Autopilot Terms</h5>
              <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full bg-cede-warning/20 text-cede-warning">
                Beta Feature
              </span>
            </div>
          </div>

          <p className="text-sm text-cede-text-secondary mb-4">
            Please read and acknowledge the following terms before using the Autopilot feature.
          </p>

          {/* CGU Checkboxes */}
          <div className="flex flex-col flex-1 gap-3">
            <CguCheckboxItem
              id="risk-acknowledgment"
              checked={riskAcknowledged}
              onCheckedChange={setRiskAcknowledged}
              icon={<AlertTriangle className="w-3.5 h-3.5 text-cede-warning" />}
              title="Risk Acknowledgment"
              description="I understand this is a beta feature that may contain bugs. Trading involves financial risk."
            />

            <CguCheckboxItem
              id="automated-trading"
              checked={automatedTradingAcknowledged}
              onCheckedChange={setAutomatedTradingAcknowledged}
              icon={<Bot className="w-3.5 h-3.5 text-cede-primary" />}
              title="Automated Trading"
              description="I authorize automated trades on my behalf. Trades stop when epoch ends, max volume is reached, or a trade fails."
            />

            {/* Terms and Conditions with link */}
            <label
              htmlFor="terms-and-conditions-widget"
              className={cn(
                "flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-all duration-200",
                "hover:border-cede-primary/30 hover:bg-cede-primary/5",
                termsAndConditionsAccepted
                  ? "border-cede-primary bg-cede-primary/10"
                  : "border-cede-border-container bg-cede-bg-component",
              )}
            >
              <Checkbox
                id="terms-and-conditions-widget"
                checked={termsAndConditionsAccepted}
                onCheckedChange={setTermsAndConditionsAccepted}
                className="mt-0.5"
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <FileText className="w-3.5 h-3.5 text-cede-text-secondary" />
                  <span className="font-medium text-sm text-cede-text-primary">Terms and Conditions</span>
                </div>
                <p className="text-xs text-cede-text-secondary leading-relaxed">
                  I have read and accept the{" "}
                  <Link
                    href={ROUTES.TERMS_AND_CONDITIONS}
                    target="_blank"
                    className="text-cede-primary hover:underline"
                    onClick={(e) => e.stopPropagation()}
                  >
                    Terms and Conditions
                  </Link>
                  .
                </p>
              </div>
            </label>
          </div>

          <MainFooter>
            <Button
              size={isMobile ? "large" : "medium"}
              disabled={!cguCanProceed || isCguSubmitting}
              onClick={handleCguSubmit}
              className="w-full"
            >
              {isCguSubmitting ? "Confirming..." : "Accept & Continue"}
            </Button>
          </MainFooter>
        </div>
      </Page>
    );
  }

  // Queue Waiting View - user is opted into the queue, waiting to be picked.
  // Layout mirrors BlockedAccessMessage: large centered icon → h5 headline →
  // body1 copy → action button, with the same spacing rhythm.
  if (currentView === AUTOMATIC_STREAK_WIDGET_VIEW.QUEUE_WAITING) {
    const isPrioritized = priorityCounter > 0;
    return (
      <Page displayFlex className={AUTOPILOT_PAGE_PADDING}>
        <div className="flex flex-col flex-1 items-center justify-center h-full p-[32px] gap-[40px]">
          <AnimatedCedeLogo width={90} height={90} shadowColor="blue" />

          <div className="flex flex-col items-center gap-3">
            <h5 className="h5 text-center">
              You&apos;re in the <span className="text-cede-primary">autopilot queue</span>
            </h5>
            {isPrioritized && (
              <div className="flex items-center gap-1.5 rounded-full border border-medal-gold/35 bg-medal-gold/10 px-3 py-1 text-medal-gold">
                <Zap className="w-3.5 h-3.5" fill="currentColor" />
                <span className="text-xs font-semibold">
                  Priority for the next {priorityCounter} epoch{priorityCounter > 1 ? "s" : ""}
                </span>
              </div>
            )}
          </div>

          <p className="body1 text-center">
            {isPrioritized
              ? "Your priority tickets move you up the queue. Your autopilot will start as soon as a slot opens up."
              : "We'll start your autopilot as soon as a slot opens up. Selection is random, so there's nothing more to do on your side."}
          </p>

          <div className="flex flex-col gap-3 w-full max-w-[300px]">
            {priorityTicketBalance > 0 && (
              <Button
                variant="primary"
                size={isMobile ? "large" : "medium"}
                disabled={isIncreasing || !campaignName}
                onClick={() => {
                  if (campaignName) {
                    increaseQueuePriority({ campaignName: campaignName as string, ticketsToSpend: 1 }).catch(() => {
                      // Error already handled by useIncreaseQueuePriority's onError callback (snackbar)
                    });
                  }
                }}
                className={cn(
                  "flex w-full items-center justify-center gap-1.5",
                  "border border-medal-gold/40 bg-medal-gold/10 text-medal-gold",
                  "hover:border-medal-gold/70 hover:bg-medal-gold/20",
                  "active:bg-medal-gold/25",
                  "hover:shadow-[0_0_16px_color-mix(in_srgb,var(--color-medal-gold)_25%,transparent)]",
                )}
              >
                <Zap className="w-4 h-4" fill="currentColor" />
                <span>{isIncreasing ? "Boosting..." : "Boost priority"}</span>
                {!isIncreasing && (
                  <span className="inline-flex min-w-[20px] items-center justify-center rounded-full border border-medal-gold/40 bg-medal-gold/15 px-1.5 py-0.5 text-[11px] font-semibold leading-none tabular-nums">
                    {priorityTicketBalance}
                  </span>
                )}
              </Button>
            )}
            <Button
              variant="secondary"
              size={isMobile ? "large" : "medium"}
              disabled={isLeaving || !campaignName}
              onClick={() => {
                if (campaignName) {
                  leaveStreakQueue(campaignName as string).catch(() => {
                    // Error already handled by useLeaveStreakQueue's onError callback (snackbar)
                  });
                }
              }}
              className="w-full"
            >
              {isLeaving ? "Leaving..." : "Leave queue"}
            </Button>
            {isPrioritized && (
              <p className="text-xs text-center text-cede-text-tertiary">
                Leaving the queue refunds your unused priority tickets.
              </p>
            )}
          </div>
        </div>
      </Page>
    );
  }

  // Progress View - show active streak(s)
  if (currentView === AUTOMATIC_STREAK_WIDGET_VIEW.PROGRESS) {
    return (
      <Page displayFlex className={AUTOPILOT_PAGE_PADDING}>
        <div className="flex flex-col flex-1">
          {/* Show selected streak progress or list of active streaks */}
          <div className="flex flex-col flex-1 gap-3 overflow-y-auto">
            {activeStreakToShow ? (
              <>
                {/* Back to all autopilots button - always show when viewing a selected streak */}
                <div className="mb-1">
                  <button
                    onClick={() => setSelectedStreakId(null)}
                    className="inline-flex items-center gap-1 text-xs text-cede-text-secondary hover:text-cede-primary transition-colors"
                  >
                    <ChevronLeft className="w-3 h-3" />
                    <span>{`View All Autopilots (${currentEpochStreaks.length + nextEpochStreaks.length})`}</span>
                  </button>
                </div>
                {isV2Streak(activeStreakToShow) ? (
                  <AutomaticStreakProgressV2
                    streak={activeStreakToShow}
                    onCancel={() => handleCancelStreak(activeStreakToShow.id)}
                    isCancelling={isCancelling}
                    currentEpochId={currentEpoch?.id}
                    spreadThreshold={campaign?.autopilotSpreadThreshold}
                    bare
                  />
                ) : (
                  <AutomaticStreakProgress
                    streak={activeStreakToShow}
                    onCancel={() => handleCancelStreak(activeStreakToShow.id)}
                    isCancelling={isCancelling}
                    currentEpochId={currentEpoch?.id}
                    spreadThreshold={campaign?.autopilotSpreadThreshold}
                    bare
                  />
                )}
              </>
            ) : (
              <>
                {/* Current Epoch Section - shown first */}
                {currentEpochStreaks.length > 0 && (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-xs text-cede-text-secondary">
                      <div className="w-2 h-2 rounded-full bg-cede-primary animate-pulse" />
                      <span className="font-medium">Current Epoch</span>
                    </div>
                    {currentEpochStreaks.map((streak) =>
                      isV2StreakSummary(streak) ? (
                        <AutomaticStreakCardV2
                          key={streak.id}
                          streak={streak}
                          onClick={() => handleStreakCardClick(streak.id)}
                          currentEpochId={currentEpoch?.id}
                        />
                      ) : (
                        <AutomaticStreakCard
                          key={streak.id}
                          streak={streak}
                          onClick={() => handleStreakCardClick(streak.id)}
                          currentEpochId={currentEpoch?.id}
                        />
                      ),
                    )}
                  </div>
                )}

                {/* Next Epoch Section - shown second */}
                {nextEpochStreaks.length > 0 && (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-xs text-cede-text-secondary pt-2">
                      <Clock className="w-3 h-3" />
                      <span className="font-medium">Scheduled for Next Epoch</span>
                    </div>
                    {nextEpochStreaks.map((streak) =>
                      isV2StreakSummary(streak) ? (
                        <AutomaticStreakCardV2
                          key={streak.id}
                          streak={streak}
                          onClick={() => handleStreakCardClick(streak.id)}
                          currentEpochId={currentEpoch?.id}
                          hideNextEpochBanner
                        />
                      ) : (
                        <AutomaticStreakCard
                          key={streak.id}
                          streak={streak}
                          onClick={() => handleStreakCardClick(streak.id)}
                          currentEpochId={currentEpoch?.id}
                          hideNextEpochBanner
                        />
                      ),
                    )}
                  </div>
                )}
              </>
            )}
          </div>

          {/* Footer buttons based on streak state */}
          {selectedStreak &&
            (
              [
                AUTOMATIC_STREAK_STATUS.COMPLETED,
                AUTOMATIC_STREAK_STATUS.CANCELLED,
                AUTOMATIC_STREAK_STATUS.STOPPED,
              ] as readonly string[]
            ).includes(selectedStreak.status) && (
              <MainFooter>
                {pendingNextEpochStreak ? (
                  // There's already a streak scheduled for next epoch - show view button
                  <Button
                    size={isMobile ? "large" : "medium"}
                    onClick={() => handleStreakCardClick(pendingNextEpochStreak.id)}
                    className="w-full"
                  >
                    View Scheduled Autopilot
                  </Button>
                ) : (
                  // No next epoch streak - allow starting new one
                  <Button
                    size={isMobile ? "large" : "medium"}
                    onClick={() => setSelectedStreakId(null)}
                    className="w-full"
                  >
                    Start New Autopilot
                  </Button>
                )}
              </MainFooter>
            )}
        </div>
      </Page>
    );
  }

  // Config View - show configuration form
  return (
    <Page displayFlex className={AUTOPILOT_PAGE_PADDING}>
      <div className="flex flex-col flex-1">
        {/* Warning if missing data */}
        {missingDataMessage && (
          <div className="mb-4 p-3 rounded-lg border border-cede-warning/30 bg-cede-warning/10">
            <div className="flex items-center gap-2 text-sm text-cede-warning">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              {missingDataMessage}
            </div>
          </div>
        )}

        {/* Account needs reconnection — API keys can't trade (missing permission,
            IP-restricted, or revoked). Prompt a relink before starting autopilot. */}
        {needsReconnection && (
          <div className="mb-4 p-3 rounded-lg border border-cede-warning/30 bg-cede-warning/10">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5 text-cede-warning" />
              <div className="space-y-1">
                <p className="text-sm font-medium text-cede-warning">Reconnect your exchange account</p>
                <p className="text-xs text-cede-text-secondary">
                  Autopilot can&apos;t trade on this account. Your API keys are missing permissions, restricted to a
                  different IP, or have been revoked. Reconnect the account to resume.
                </p>
              </div>
            </div>
          </div>
        )}

        {canStartStreak ? (
          configVersion === 2 && autopilotConfig?.version === 2 ? (
            <AutomaticStreakConfigFormV2
              onSubmit={handleConfigSubmitV2}
              onCancel={() => {}}
              isSubmitting={isQueueDisabled ? isCreating : isJoining}
              pairSymbol={pairSymbol?.pairSymbol || ""}
              accountId={exchange?.accountId || ""}
              exchangeId={exchange?.exchangeId || ""}
              campaignName={campaignName ?? ""}
              epochId={effectiveEpoch?.id ?? 0}
              config={autopilotConfig.data}
              epochEndTime={effectiveEpoch?.end}
              hasNextEpoch={!!nextEpoch && nextEpoch.id !== effectiveEpoch?.id}
              className="flex-1 p-0"
              hideActions
              onValidityChange={setIsFormValid}
              formId={AUTOMATIC_STREAK_CONFIG_FORM_V2_ID}
              spreadThreshold={campaign?.autopilotSpreadThreshold}
              isEpochLimitReached={isEpochLimitReached}
              showPriorityBoost={!isQueueDisabled}
            />
          ) : autopilotConfig?.version === 1 ? (
            <AutomaticStreakConfigForm
              onSubmit={handleConfigSubmitV1}
              onCancel={() => {}}
              isSubmitting={isCreating}
              pairSymbol={pairSymbol?.pairSymbol || ""}
              accountId={exchange?.accountId || ""}
              exchangeId={exchange?.exchangeId || ""}
              config={autopilotConfig.data}
              className="flex-1 p-0"
              hideActions
              onValidityChange={setIsFormValid}
            />
          ) : null
        ) : (
          <div className="flex flex-col flex-1 items-center justify-center gap-4">
            <div className="p-3 rounded-full bg-cede-primary/20">
              <Bot className="w-8 h-8 text-cede-primary" />
            </div>
            <div className="text-center">
              <h3 className="font-medium text-cede-text-primary mb-1">Autopilot</h3>
              <p className="text-sm text-cede-text-secondary">Automate your trading</p>
            </div>
          </div>
        )}

        {canStartStreak && (
          <MainFooter>
            <div className="flex flex-col gap-3 w-full">
              {/* Last kick banner — shown only when the user was previously
               * evicted from the queue by the system (not a voluntary leave)
               * and has not yet re-joined. Re-joining clears the backend
               * `leaveReason`, so this row naturally disappears. */}
              {lastKickReason && (
                <div className="p-3 rounded-lg border border-cede-warning/30 bg-cede-warning/10">
                  <div className="flex items-start gap-2 text-sm text-cede-warning">
                    <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    <span>
                      You were removed from the autopilot queue last time because{" "}
                      {describeQueueKickReason(lastKickReason)}.
                    </span>
                  </div>
                </div>
              )}
              {needsReconnection ? (
                <Button size={isMobile ? "large" : "medium"} onClick={handleReconnectAccount} className="w-full">
                  Reconnect account
                </Button>
              ) : isMissingSocials ? (
                <Button size={isMobile ? "large" : "medium"} onClick={openSocialLinkingModal} className="w-full">
                  Connect your social accounts
                </Button>
              ) : (
                <Button
                  type="submit"
                  form={
                    configVersion === 2 && autopilotConfig?.version === 2
                      ? AUTOMATIC_STREAK_CONFIG_FORM_V2_ID
                      : AUTOMATIC_STREAK_CONFIG_FORM_ID
                  }
                  size={isMobile ? "large" : "medium"}
                  disabled={!canStartStreak || isCreating || isJoining || !isFormValid || isSubmitDebounced}
                  className="w-full"
                >
                  {getSubmitButtonLabel({
                    queueSystemEnabled: configVersion === 2 && autopilotConfig?.version === 2 && !isQueueDisabled,
                    isCreating,
                    isJoining,
                    isSubmitDebounced,
                  })}
                </Button>
              )}
            </div>
          </MainFooter>
        )}
      </div>
    </Page>
  );
};
